import React from 'react';
import { BaseBoxProps, LayoutBox, LayoutBoxProps } from '../Box';
import { HeightMeter } from '../HeightMeter';
import { StyledAccordionItem, StyledAccordionItemContent } from './AccordionMenu.styled';

export const AccordionMenuContext = React.createContext<any>({
  isOpen: false,
  activeTabIndex: undefined,
  handleItemClick: (index: number) => (event: any) => {}
});

export type AccordionMenuProps = LayoutBoxProps & {
  children: React.ReactNode;
  initialActiveIndex?: number;
};

export const AccordionMenu = ({ children, initialActiveIndex, ...styledProps }: AccordionMenuProps) => {
  const [isOpen, setIsOpen] = React.useState(initialActiveIndex !== undefined);
  const [activeTabIndex, setActiveTabIndex] = React.useState<undefined | number>(initialActiveIndex);

  const handleItemClick = React.useCallback(
    index => () => {
      setIsOpen(prevState => (activeTabIndex === index ? !prevState : true));
      setActiveTabIndex(activeTabIndex === index ? null : index);
    },
    [activeTabIndex]
  );

  return (
    <AccordionMenuContext.Provider value={{ isOpen, activeTabIndex, handleItemClick }}>
      <LayoutBox as="ul" m={0} p={0} {...styledProps}>
        {React.Children.map(children, (child, index) => child && React.cloneElement(child, { index }))}
      </LayoutBox>
    </AccordionMenuContext.Provider>
  );
};

type AccordionItemProps = BaseBoxProps & { children: React.ReactNode; index?: number };
const AccordionItem = ({ children, index, ...styledProps }: AccordionItemProps) => {
  return (
    <StyledAccordionItem {...styledProps}>
      {React.Children.map(children, child => child && React.cloneElement(child, { index }))}
    </StyledAccordionItem>
  );
};

type AccordionItemToggleProps = { children: React.ReactNode; index?: any };
const AccordionItemToggle = ({ children, index }: AccordionItemToggleProps) => {
  const { activeTabIndex, handleItemClick } = React.useContext(AccordionMenuContext);
  const isActive = activeTabIndex === index;

  return (
    <div onClick={handleItemClick(index)}>
      {React.Children.map(children, child => child && React.cloneElement(child, { index, isActive }))}
    </div>
  );
};

type AccordionItemContentProps = {
  children: React.ReactNode;
  index?: any;
};
const AccordionItemContent = ({ children, index, ...styledProps }: AccordionItemContentProps) => {
  const { isOpen, activeTabIndex } = React.useContext(AccordionMenuContext);
  const [maxHeight, setMaxHeight] = React.useState(0);
  const isActive = activeTabIndex === index;

  return (
    <StyledAccordionItemContent
      className={isActive && 'active'}
      isActive={isActive}
      isOpen={isOpen}
      maxHeight={maxHeight}
      {...styledProps}
    >
      <HeightMeter onChange={setMaxHeight}>{children}</HeightMeter>
    </StyledAccordionItemContent>
  );
};

AccordionMenu.AccordionItem = AccordionItem;
AccordionMenu.AccordionItemToggle = AccordionItemToggle;
AccordionMenu.AccordionItemContent = AccordionItemContent;
